import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FiPlus, FiSearch, FiUsers } from 'react-icons/fi';
import { FormGroup, FormControl } from 'react-bootstrap';
import Layout from 'components/Layout';
import GroupList from 'components/Groups';
import GroupEdit from 'components/Groups/GroupEditModal';
import Loading from 'components/Loading';
import Modal from 'components/Modal';
import { showModal, hideModal } from 'actions/modalActions';
import * as groupsActions from 'actions/groupsActions';
import * as calendarsActions from 'actions/calendarsActions';
import { groupsService } from 'services';
import { helpUrls } from 'utils/helpUrls';
import HelpBanner from 'components/HelpBanner';
import { withTranslation } from 'react-i18next';

const GROUPS_PAGE_SIZE = 40;
const GROUPS_INFINITE_SCROLL_TARGET_ID = 'groups-infinite-scroll-target';

class Groups extends Component {
	constructor(props) {
		super(props);

		this.state = {
			accumulatedGroups: [],
			listPage: 0,
			hasMore: true,
			groupsLoading: false,
			groupsLoadingMore: false,
			listLoadError: null,
			search_groups: '',
		};

		this.closeModal = this.closeModal.bind(this);
		this.openNewGroupModal = this.openNewGroupModal.bind(this);
		this.getGroups = this.getGroups.bind(this);
		this.loadGroupList = this.loadGroupList.bind(this);
		this.fetchMoreGroups = this.fetchMoreGroups.bind(this);
		this.handleChangeSearch = this.handleChangeSearch.bind(this);
	}

	handleChangeSearch(event) {
		this.setState({ search_groups: event.target.value }, () => {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				this.loadGroupList(false);
			}, 500);
		});
	}

	componentDidMount() {
		this.props.getSelectableCalendars();
		this.getGroups();
	}

	async getGroups() {
		this.props.getChildGroups();
		await this.loadGroupList(false);
	}

	async loadGroupList(append) {
		if (append) {
			if (
				!this.state.hasMore ||
				this.state.groupsLoadingMore ||
				this.state.groupsLoading
			) {
				return;
			}
		}

		const pageToFetch = append ? this.state.listPage + 1 : 0;

		if (!append) {
			this.setState({
				accumulatedGroups: [],
				listPage: 0,
				hasMore: true,
				groupsLoading: true,
				listLoadError: null,
			});
		} else {
			this.setState({ groupsLoadingMore: true });
		}

		try {
			const res = await groupsService.getAll({
				sort: '+name',
				variant: 'detailed',
				page: pageToFetch,
				page_size: GROUPS_PAGE_SIZE,
				q: this.state.search_groups,
			});

			if (!res.ok) {
				throw new Error(this.props.t('groups.errorLoading'));
			}

			const newRows = Array.isArray(res.data) ? res.data : [];
			const totalItems = res.pagination?.total_items ?? 0;

			const accumulated = append
				? [...this.state.accumulatedGroups, ...newRows]
				: newRows;

			let hasMore = false;
			if (newRows.length > 0) {
				if (newRows.length < GROUPS_PAGE_SIZE) {
					hasMore = false;
				} else if (totalItems > 0) {
					hasMore = accumulated.length < totalItems;
				} else {
					hasMore = true;
				}
			}

			this.setState({
				accumulatedGroups: accumulated,
				listPage: pageToFetch,
				hasMore,
				groupsLoading: false,
				groupsLoadingMore: false,
				listLoadError: null,
			});
		} catch (error) {
			console.error('Error al obtener grupos:', error);
			if (!append) {
				this.setState({
					groupsLoading: false,
					groupsLoadingMore: false,
					listLoadError:
						error.message || this.props.t('groups.errorLoading'),
				});
			} else {
				this.setState({
					groupsLoading: false,
					groupsLoadingMore: false,
					hasMore: false,
				});
			}
		}
	}

	fetchMoreGroups = () => {
		this.loadGroupList(true);
	};

	closeModal() {
		this.props.hideModal();
	}

	openNewGroupModal() {
		this.props.showModal(
			{
				open: true,
				title: this.props.t('groups.newGroup'),
				style: { width: '400px' },
				content: (
					<GroupEdit getGroups={this.getGroups} handleClose={this.closeModal} />
				),
				closeModal: this.closeModal,
			},
			'edit'
		);
	}

	getContent = () => {
		const {
			accumulatedGroups,
			groupsLoading,
			groupsLoadingMore,
			hasMore,
			listLoadError,
		} = this.state;

		if (groupsLoading && accumulatedGroups.length === 0) {
			return <Loading />;
		}

		if (listLoadError) {
			return <div className="no-results">{listLoadError}</div>;
		}

		if (
			accumulatedGroups.length === 0 &&
			!groupsLoading &&
			this.state.search_groups.trim()
		) {
			return <div className="no-results">{this.props.t('common.noResults')}</div>;
		}

		if (accumulatedGroups.length === 0 && !groupsLoading) {
			return (
				<div className="first-steps">
					<FiUsers className="icono-pasos" />
					<h3 className="title2">{this.props.t('groups.firstSteps.title')}</h3>

					<p>
						{this.props.t('groups.firstSteps.desc1')}{' '}
						<strong>{this.props.t('groups.firstSteps.employeeGroup')}</strong>{' '}
						{this.props.t('groups.firstSteps.desc2')}{' '}
						<a className="alink" target="_blank" href={helpUrls.group}>
							{helpUrls.group}
						</a>
					</p>
				</div>
			);
		}

		return (
			<GroupList
				groups={accumulatedGroups}
				hasMore={hasMore}
				loadingMore={groupsLoadingMore}
				fetchMore={this.fetchMoreGroups}
				reloadGroups={this.getGroups}
				scrollTargetId={GROUPS_INFINITE_SCROLL_TARGET_ID}
			/>
		);
	};

	render() {
		const { search_groups } = this.state;

		return (
			<>
				<Layout configSidebar={true} className="page-groups" context="groups">
					<div className="heading">
						<h1 className="title">{this.props.t('groups.title')}</h1>
						<div className="heading-help-banner heading-config hidden-sm">
							<HelpBanner
								title={this.props.t('common.needHelp')}
								text={this.props.t('common.helpText')}
								linkText={this.props.t('common.moreInfo')}
								linkHref="https://help.plain.ninja/es/collections/12657247-grupos"
							/>
						</div>

						<div className="heading-filters">
							<FormGroup
								className="form-group form-search"
								controlId="search_groups"
							>
								<FormControl
									type="text"
									placeholder={this.props.t('common.searchPlaceholder')}
									value={search_groups}
									onChange={this.handleChangeSearch}
								/>
								<FiSearch />
							</FormGroup>
						</div>

						<div className="heading-actions">
							<button
								type="button"
								title={this.props.t('groups.createGroup')}
								onClick={this.openNewGroupModal}
								className="btn btn-new"
							>
								<FiPlus />
							</button>
						</div>
					</div>

					<div className="groups-page-content">{this.getContent()}</div>
				</Layout>

				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.calendarsReducer,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	getChildGroups: () => dispatch(groupsActions.getChildGroups()),
	getSelectableCalendars: () =>
		dispatch(calendarsActions.getSelectableCalendars()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Groups));
